import {match} from 'ts-pattern';

import type {DataGridRow} from '@dms/api/core';
import {businessCaseRoutes, interestRoutes, workshopRoutes} from '@dms/routes';

import {composePath} from 'shared/web';

import type {CustomerInsightsRecord} from '../types/CustomerInsightsViewModel';
import {CUSTOMER_INSIGHTS_GRID_FIELDS} from './customerInsightsGridFields';
import {getBusinessCaseRowType} from './getBusinessCaseRowType';
import {readDataGridCellDateString} from './readDataGridCellDateString';
import {readDataGridCellEntityLabel} from './readDataGridCellEntityLabel';
import {readDataGridCellKey} from './readDataGridCellKey';
import {readDataGridCellKeyLabel} from './readDataGridCellKeyLabel';
import {readDataGridCellString} from './readDataGridCellString';

const getRecordCode = (row: DataGridRow, kind: CustomerInsightsRecord['kind']) =>
  match(kind)
    .with(
      'serviceCase',
      () => readDataGridCellString(row, CUSTOMER_INSIGHTS_GRID_FIELDS.serviceCase.code) ?? row.id
    )
    .with(
      'businessCase',
      () => readDataGridCellString(row, CUSTOMER_INSIGHTS_GRID_FIELDS.businessCase.code) ?? row.id
    )
    .otherwise(
      () => readDataGridCellString(row, CUSTOMER_INSIGHTS_GRID_FIELDS.interest.code) ?? row.id
    );

const getRecordFields = (kind: CustomerInsightsRecord['kind']) =>
  match(kind)
    .with('businessCase', () => CUSTOMER_INSIGHTS_GRID_FIELDS.businessCase)
    .with('interest', () => CUSTOMER_INSIGHTS_GRID_FIELDS.interest)
    .with('serviceCase', () => CUSTOMER_INSIGHTS_GRID_FIELDS.serviceCase)
    .exhaustive();

const getRecordVehicleTitle = (row: DataGridRow, kind: CustomerInsightsRecord['kind']) =>
  readDataGridCellEntityLabel(row, getRecordFields(kind).vehicleTitle);

const getRecordSubtitle = (row: DataGridRow, kind: CustomerInsightsRecord['kind']) =>
  match(kind)
    .with('serviceCase', () =>
      readDataGridCellString(row, CUSTOMER_INSIGHTS_GRID_FIELDS.serviceCase.subtitle)
    )
    .otherwise(() => undefined);

const getRecordType = (row: DataGridRow, kind: CustomerInsightsRecord['kind']) =>
  match(kind)
    .with('businessCase', () => getBusinessCaseRowType(row))
    .with('interest', () => readDataGridCellKey(row, CUSTOMER_INSIGHTS_GRID_FIELDS.interest.type))
    .otherwise(() => undefined);

const getRecordOccurredAt = (row: DataGridRow, kind: CustomerInsightsRecord['kind']) =>
  readDataGridCellDateString(row, getRecordFields(kind).occurredAt);

const getRecordHref = (
  row: DataGridRow,
  kind: CustomerInsightsRecord['kind'],
  state: string | undefined
) =>
  match({kind, state})
    .with({kind: 'businessCase'}, () =>
      composePath(businessCaseRoutes.overview, {
        params: {id: row.id},
        isSearchParamsPreserved: false,
      })
    )
    .with({kind: 'interest'}, () =>
      composePath(interestRoutes.detailOverview, {
        params: {id: row.id},
        isSearchParamsPreserved: false,
      })
    )
    .with({kind: 'serviceCase', state: 'DRAFT'}, () =>
      composePath(workshopRoutes.draftServiceCase, {
        params: {id: row.id},
        isSearchParamsPreserved: false,
      })
    )
    .with({kind: 'serviceCase'}, () =>
      composePath(workshopRoutes.serviceCaseDetail, {
        params: {id: row.id},
        isSearchParamsPreserved: false,
      })
    )
    .otherwise(() => undefined);

export function mapDataGridRow(
  row: DataGridRow,
  kind: CustomerInsightsRecord['kind'] = 'businessCase'
): CustomerInsightsRecord {
  const fields = getRecordFields(kind);
  const state = readDataGridCellKey(row, fields.state);
  const code = getRecordCode(row, kind);
  const vehicleTitle = getRecordVehicleTitle(row, kind);
  const leadQuality = match(kind)
    .with('interest', () =>
      readDataGridCellKeyLabel(row, CUSTOMER_INSIGHTS_GRID_FIELDS.interest.leadQuality)
    )
    .otherwise(() => undefined);

  return {
    id: row.id,
    kind,
    title: vehicleTitle ?? code,
    code,
    subtitle: getRecordSubtitle(row, kind),
    type: getRecordType(row, kind),
    state,
    ...(leadQuality === undefined ? {} : {leadQuality}),
    occurredAt: getRecordOccurredAt(row, kind),
    owner: readDataGridCellEntityLabel(row, fields.owner),
    href: getRecordHref(row, kind, state),
  };
}
